/**
 * Tipos e constantes para sistema de referências externas em fluxos
 * Utilizado na exportação/importação de fluxos entre tenants
 * 
 * RELEASE: 1.0.0 - 2026-02-03 - Infraestrutura base para export/import
 */

/**
 * Tipos de referências externas que podem existir em nodes de fluxo
 * Cada tipo corresponde a uma entidade que precisa ser mapeada na importação
 */
export type ExternalReferenceType =
  | 'agent'              // Usuário/Agente (profiles via tenant_memberships)
  | 'department'         // Departamento
  | 'workspace'          // CRM Workspace
  | 'pipeline'           // CRM Pipeline
  | 'phase'              // CRM Phase (fase do pipeline)
  | 'distribution_queue' // Fila de distribuição
  | 'contact_tag'        // Tag de contato
  | 'deal_tag'           // Tag de negócio CRM
  | 'inbox'              // Inbox (canal de comunicação)
  | 'kanban_category'    // Categoria de fase de conversa (Kanban)
  | 'kanban_phase'       // Fase de conversa (Kanban)
  | 'credential'         // Credencial do tenant (API keys, etc.)
  | 'template'           // Template WhatsApp
  | 'seller'             // Vendedor CRM
  | 'tenant_variable'    // Variável global do tenant (sistema ou personalizada)
  | 'media_gallery'      // Mídia da galeria interna (RELEASE: 1.2.0)
  | 'published_contract' // Contrato publicado (RELEASE: 2.0.0)
  | 'meio_pagamento'     // Meio de pagamento financeiro (RELEASE: 2.0.0)
  | 'conta_corrente'     // Conta corrente financeira (RELEASE: 2.0.0)
  | 'crm_product'        // Produto do catálogo CRM (RELEASE: 2.0.0)
  // RELEASE: 3.0.0 - 2026-05-31 - Suporte ao node Criar Tarefa (task_create)
  | 'task_category'      // Categoria do módulo Tarefas
  | 'task_phase'         // Fase do módulo Tarefas (pertence a uma categoria)
  | 'task_classification'; // Classificação opcional do módulo Tarefas

/**
 * Configuração de mapeamento para cada tipo de referência
 * Define a tabela destino e campo para match por nome
 */
export interface ReferenceTypeConfig {
  /** Tipo da referência */
  type: ExternalReferenceType;
  /** Prefixo do placeholder (ex: "__REF:agent:") */
  prefix: string;
  /** Tabela do Supabase onde buscar equivalentes */
  table: string;
  /** Campo da tabela para match por nome */
  nameField: string;
  /** Label amigável em português */
  label: string;
  /** Descrição para UI */
  description: string;
  /** Se a referência é obrigatória (bloqueia ativação do fluxo) */
  required: boolean;
  /** Ícone Lucide para UI */
  icon: string;
}

/**
 * Mapeamento completo de configurações por tipo de referência
 */
export const REFERENCE_TYPE_CONFIGS: Record<ExternalReferenceType, ReferenceTypeConfig> = {
  agent: {
    type: 'agent',
    prefix: '__REF:agent:',
    table: 'profiles',
    nameField: 'full_name',
    label: 'Agente/Usuário',
    description: 'Usuários do sistema para transferência de conversas',
    required: false,
    icon: 'User',
  },
  department: {
    type: 'department',
    prefix: '__REF:department:',
    table: 'departments',
    nameField: 'name',
    label: 'Departamento',
    description: 'Departamentos para organização de atendimento',
    required: false,
    icon: 'Building2',
  },
  workspace: {
    type: 'workspace',
    prefix: '__REF:workspace:',
    table: 'crm_workspaces',
    nameField: 'name',
    label: 'Workspace CRM',
    description: 'Workspace do CRM para criação de negócios',
    required: true,
    icon: 'Briefcase',
  },
  pipeline: {
    type: 'pipeline',
    prefix: '__REF:pipeline:',
    table: 'crm_pipelines',
    nameField: 'name',
    label: 'Pipeline CRM',
    description: 'Pipeline de vendas',
    required: true,
    icon: 'GitBranch',
  },
  phase: {
    type: 'phase',
    prefix: '__REF:phase:',
    table: 'crm_phases',
    nameField: 'name',
    label: 'Fase CRM',
    description: 'Fase do pipeline de vendas',
    required: true,
    icon: 'Layers',
  },
  distribution_queue: {
    type: 'distribution_queue',
    prefix: '__REF:queue:',
    table: 'distribution_queues',
    nameField: 'name',
    label: 'Fila de Distribuição',
    description: 'Fila para distribuição automática de leads',
    required: false,
    icon: 'Users',
  },
  contact_tag: {
    type: 'contact_tag',
    prefix: '__REF:contact_tag:',
    table: 'contact_tags',
    nameField: 'name',
    label: 'Tag de Contato',
    description: 'Tag para categorização de contatos',
    required: false,
    icon: 'Tag',
  },
  deal_tag: {
    type: 'deal_tag',
    prefix: '__REF:deal_tag:',
    table: 'deal_tags',
    nameField: 'name',
    label: 'Tag de Negócio',
    description: 'Tag para categorização de negócios CRM',
    required: false,
    icon: 'Tag',
  },
  inbox: {
    type: 'inbox',
    prefix: '__REF:inbox:',
    table: 'inboxes',
    nameField: 'name',
    label: 'Inbox/Canal',
    description: 'Canal de comunicação (WhatsApp, etc.)',
    required: true, // Templates requerem inbox específico
    icon: 'Inbox',
  },
  kanban_category: {
    type: 'kanban_category',
    prefix: '__REF:kanban_cat:',
    table: 'kanban_categories',
    nameField: 'name',
    label: 'Categoria Kanban',
    description: 'Categoria de fase de conversa',
    required: false,
    icon: 'Layout',
  },
  kanban_phase: {
    type: 'kanban_phase',
    prefix: '__REF:kanban_phase:',
    table: 'kanban_phases',
    nameField: 'name',
    label: 'Fase Kanban',
    description: 'Fase de conversa no Kanban',
    required: false,
    icon: 'Columns',
  },
  credential: {
    type: 'credential',
    prefix: '__REF:credential:',
    table: 'tenant_credentials',
    nameField: 'name',
    label: 'Credencial',
    description: 'Credencial de API externa',
    required: false,
    icon: 'Key',
  },
  template: {
    type: 'template',
    prefix: '__REF:template:',
    table: 'whatsapp_templates',
    nameField: 'name',
    label: 'Template WhatsApp',
    description: 'Template oficial do WhatsApp Business',
    required: true,
    icon: 'FileText',
  },
  seller: {
    type: 'seller',
    prefix: '__REF:seller:',
    table: 'crm_sellers',
    nameField: 'name',
    label: 'Vendedor',
    description: 'Vendedor do CRM',
    required: false,
    icon: 'UserCheck',
  },
  tenant_variable: {
    type: 'tenant_variable',
    prefix: '__REF:variable:',
    table: 'tenant_variables',
    nameField: 'name',
    label: 'Variável',
    description: 'Variável global do tenant (sistema ou personalizada)',
    required: false,
    icon: 'Variable',
  },
  // RELEASE: 1.2.0 - 2026-02-03 - Suporte a mídias da galeria interna
  media_gallery: {
    type: 'media_gallery',
    prefix: '__REF:media:',
    table: 'media_gallery',
    nameField: 'name',
    label: 'Mídia da Galeria',
    description: 'Arquivo de mídia (imagem, vídeo, áudio, documento) da galeria do tenant',
    required: false,
    icon: 'Image',
  },
  // RELEASE: 2.0.0 - 2026-03-22 - Suporte a contratos publicados
  published_contract: {
    type: 'published_contract',
    prefix: '__REF:pub_contract:',
    table: 'published_contracts',
    nameField: 'name',
    label: 'Contrato Publicado',
    description: 'Contrato publicado para geração automática',
    required: true,
    icon: 'FileSignature',
  },
  // RELEASE: 2.0.0 - 2026-03-22 - Suporte a meios de pagamento
  meio_pagamento: {
    type: 'meio_pagamento',
    prefix: '__REF:meio_pag:',
    table: 'financeiro_meios_pagamento',
    nameField: 'nome',
    label: 'Meio de Pagamento',
    description: 'Meio de pagamento financeiro (PIX, boleto, cartão)',
    required: true,
    icon: 'CreditCard',
  },
  // RELEASE: 2.0.0 - 2026-03-22 - Suporte a contas correntes
  conta_corrente: {
    type: 'conta_corrente',
    prefix: '__REF:conta:',
    table: 'financeiro_conta_corrente',
    nameField: 'nome',
    label: 'Conta Corrente',
    description: 'Conta corrente financeira do tenant',
    required: true,
    icon: 'Landmark',
  },
  // RELEASE: 2.0.0 - 2026-03-22 - Suporte a produtos CRM
  crm_product: {
    type: 'crm_product',
    prefix: '__REF:product:',
    table: 'crm_products',
    nameField: 'name',
    label: 'Produto CRM',
    description: 'Produto do catálogo CRM para cobrança',
    required: false,
    icon: 'ShoppingCart',
  },
  // RELEASE: 3.0.0 - 2026-05-31 - Suporte ao módulo Tarefas (node task_create)
  task_category: {
    type: 'task_category',
    prefix: '__REF:task_cat:',
    table: 'task_categories',
    nameField: 'name',
    label: 'Categoria de Tarefa',
    description: 'Categoria do módulo Tarefas onde a tarefa será criada',
    required: true,
    icon: 'ListTodo',
  },
  task_phase: {
    type: 'task_phase',
    prefix: '__REF:task_phase:',
    table: 'task_phases',
    nameField: 'name',
    label: 'Fase de Tarefa',
    description: 'Fase inicial da tarefa (vinculada à categoria)',
    required: true,
    icon: 'Columns',
  },
  task_classification: {
    type: 'task_classification',
    prefix: '__REF:task_class:',
    table: 'task_classifications',
    nameField: 'name',
    label: 'Classificação de Tarefa',
    description: 'Classificação opcional da tarefa',
    required: false,
    icon: 'Tag',
  },
};

/**
 * Referência externa extraída de um fluxo
 * Representa uma entidade que precisa ser mapeada na importação
 */
export interface ExternalReference {
  /** Placeholder no formato __REF:type:name__ */
  placeholder: string;
  /** Tipo da referência */
  type: ExternalReferenceType;
  /** UUID original (do tenant de origem) */
  originalId: string;
  /** Nome da entidade para match */
  name: string;
  /** Contexto onde a referência foi encontrada (para UI) */
  context: string;
  /** ID do node onde a referência foi encontrada */
  nodeId?: string;
  /** Tipo do node onde a referência foi encontrada */
  nodeType?: string;
}

/**
 * Mapeamento de referência: placeholder → UUID real no tenant destino
 */
export interface ReferenceMapping {
  /** Placeholder original (ex: "__REF:agent:João__") */
  placeholder: string;
  /** UUID mapeado no tenant destino */
  mappedId: string | null;
  /** Nome da entidade mapeada (para confirmação visual) */
  mappedName?: string;
  /** Se o mapeamento foi automático (match por nome) ou manual */
  autoMatched: boolean;
}

/**
 * Resultado de tentativa de auto-match para uma referência
 */
export interface AutoMatchResult {
  /** Referência original */
  reference: ExternalReference;
  /** Se encontrou match exato por nome */
  exactMatch: boolean;
  /** Matches encontrados (pode ser múltiplos para fuzzy) */
  candidates: Array<{
    id: string;
    name: string;
    similarity: number; // 0-1, 1 = exato
  }>;
}

/**
 * Gera um placeholder formatado para uma referência externa
 * 
 * @param type - Tipo da referência
 * @param name - Nome da entidade
 * @returns Placeholder no formato __REF:type:name__
 */
export function createPlaceholder(type: ExternalReferenceType, name: string): string {
  const config = REFERENCE_TYPE_CONFIGS[type];
  // Sanitiza o nome removendo caracteres especiais que podem quebrar o parsing
  const sanitizedName = name.replace(/[_:]/g, ' ').trim();
  return `${config.prefix}${sanitizedName}__`;
}

/**
 * Extrai tipo e nome de um placeholder
 * 
 * @param placeholder - Placeholder no formato __REF:type:name__
 * @returns Objeto com type e name, ou null se inválido
 */
export function parsePlaceholder(placeholder: string): { type: ExternalReferenceType; name: string } | null {
  // Padrão: __REF:tipo:nome__
  const match = placeholder.match(/^__REF:([a-z_]+):(.+)__$/);
  if (!match) return null;
  
  const [, typeStr, name] = match;
  const type = typeStr as ExternalReferenceType;
  
  // Valida se é um tipo conhecido
  if (!REFERENCE_TYPE_CONFIGS[type]) return null;
  
  return { type, name };
}

/**
 * Verifica se um valor é um placeholder de referência externa
 */
export function isPlaceholder(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  return value.startsWith('__REF:') && value.endsWith('__');
}

/**
 * Campos de nodes que podem conter UUIDs de referências externas
 * Mapeamento: nodeType → caminho no config → tipo de referência
 */
/**
 * Campos de nodes que podem conter UUIDs de referências externas
 * Mapeamento: nodeType → caminho no config → tipo de referência
 * 
 * RELEASE: 1.0.0 - 2026-02-03 - Mapeamento inicial de referências
 * RELEASE: 1.1.0 - 2026-02-03 - Adicionado suporte a tenant_variable
 */
export const NODE_REFERENCE_FIELDS: Record<string, Array<{ path: string; type: ExternalReferenceType; nameField?: string }>> = {
  // ========================================
  // TRANSFERÊNCIA
  // ========================================
  transfer_human: [
    { path: 'agent_id', type: 'agent', nameField: 'agent_name' },
  ],
  transfer_department: [
    { path: 'department_id', type: 'department', nameField: 'department_name' },
  ],
  
  // ========================================
  // CRM - Criar Negócio
  // ========================================
  crm_create_deal: [
    { path: 'workspace_id', type: 'workspace', nameField: 'workspace_name' },
    { path: 'pipeline_id', type: 'pipeline', nameField: 'pipeline_name' },
    { path: 'phase_id', type: 'phase', nameField: 'phase_name' },
    { path: 'seller_id', type: 'seller', nameField: 'seller_name' },
    { path: 'distribution_queue_id', type: 'distribution_queue', nameField: 'distribution_queue_name' },
    // Variáveis de saída
    { path: 'output_variable_id', type: 'tenant_variable', nameField: 'output_variable' },
    { path: 'title_variable_id', type: 'tenant_variable', nameField: 'title_variable' },
    { path: 'duplicate_result_variable_id', type: 'tenant_variable', nameField: 'duplicate_result_variable' },
  ],
  
  // ========================================
  // CRM - Alterar Fase
  // ========================================
  crm_change_phase: [
    { path: 'phase_id', type: 'phase', nameField: 'phase_name' },
    { path: 'deal_variable_id', type: 'tenant_variable', nameField: 'deal_variable_name' },
  ],
  
  // ========================================
  // CRM - Atribuir Vendedor
  // ========================================
  crm_assign_seller: [
    { path: 'seller_id', type: 'seller', nameField: 'seller_name' },
    { path: 'distribution_queue_id', type: 'distribution_queue', nameField: 'distribution_queue_name' },
    { path: 'deal_variable_id', type: 'tenant_variable', nameField: 'deal_variable_name' },
  ],
  
  // ========================================
  // TAGS
  // ========================================
  add_tag: [
    { path: 'tag_id', type: 'contact_tag', nameField: 'tag_name' },
    { path: 'deal_variable_id', type: 'tenant_variable', nameField: 'deal_variable_name' },
  ],
  remove_tag: [
    { path: 'tag_id', type: 'contact_tag', nameField: 'tag_name' },
    { path: 'deal_variable_id', type: 'tenant_variable', nameField: 'deal_variable_name' },
  ],
  
  // ========================================
  // KANBAN / FASES DE CONVERSA
  // ========================================
  assign_phase: [
    { path: 'category_id', type: 'kanban_category', nameField: 'category_name' },
    { path: 'phase_id', type: 'kanban_phase', nameField: 'phase_name' },
  ],

  // ========================================
  // TAREFAS - RELEASE: 3.0.0 - 2026-05-31 - Node Criar Tarefa
  // ========================================
  task_create: [
    { path: 'category_id', type: 'task_category', nameField: 'category_name' },
    { path: 'phase_id', type: 'task_phase', nameField: 'phase_name' },
    { path: 'classification_id', type: 'task_classification', nameField: 'classification_name' },
    { path: 'assignee_id', type: 'agent', nameField: 'assignee_name' },
    { path: 'title_variable_id', type: 'tenant_variable', nameField: 'title_variable' },
    { path: 'description_variable_id', type: 'tenant_variable', nameField: 'description_variable' },
    { path: 'due_date_variable_id', type: 'tenant_variable', nameField: 'due_date_variable' },
    { path: 'assignee_variable_id', type: 'tenant_variable', nameField: 'assignee_variable' },
    { path: 'deal_variable_id', type: 'tenant_variable', nameField: 'deal_variable' },
    { path: 'output_variable_id', type: 'tenant_variable', nameField: 'output_variable' },
  ],
  
  // ========================================
  // TEMPLATE WHATSAPP
  // ========================================
  message_template: [
    { path: 'inbox_id', type: 'inbox', nameField: 'inbox_name' },
    { path: 'template_id', type: 'template', nameField: 'template_name' },
  ],
  
  // ========================================
  // WEBHOOK
  // ========================================
  webhook: [
    { path: 'credential_id', type: 'credential', nameField: 'credential_name' },
  ],
  
  // ========================================
  // VARIÁVEIS - Definir Variável
  // ========================================
  set_variable: [
    { path: 'variable_id', type: 'tenant_variable', nameField: 'variable_name' },
  ],
  
  // ========================================
  // PERGUNTAS
  // ========================================
  question_buttons: [
    { path: 'variable_id', type: 'tenant_variable', nameField: 'variable_name' },
  ],
  question_list: [
    { path: 'variable_id', type: 'tenant_variable', nameField: 'variable_name' },
  ],
  input_data: [
    { path: 'variable_id', type: 'tenant_variable', nameField: 'variable_name' },
  ],
  
  // ========================================
  // MÍDIA - RELEASE: 1.2.0 - 2026-02-03
  // ========================================
  message_media: [
    { path: 'media_gallery_id', type: 'media_gallery', nameField: 'media_gallery_name' },
    { path: 'inbox_id', type: 'inbox', nameField: 'inbox_name' },
  ],

  // ========================================
  // CONTRATO - RELEASE: 2.0.0 - 2026-03-22
  // ========================================
  contract_generate: [
    { path: 'published_contract_id', type: 'published_contract', nameField: 'published_contract_name' },
    { path: 'deal_variable_id', type: 'tenant_variable', nameField: 'deal_variable' },
    { path: 'result_variable_id', type: 'tenant_variable', nameField: 'result_variable' },
  ],

  // ========================================
  // COBRANÇA - RELEASE: 2.0.0 - 2026-03-22
  // Nota: fixed_items[].product_id tratado via lógica especial em processNode()
  // ========================================
  charge_generate: [
    { path: 'meio_pagamento_id', type: 'meio_pagamento', nameField: 'meio_pagamento_nome' },
    { path: 'conta_corrente_id', type: 'conta_corrente', nameField: 'conta_corrente_nome' },
    { path: 'customer_variable_id', type: 'tenant_variable', nameField: 'customer_variable' },
    { path: 'deal_variable_id', type: 'tenant_variable', nameField: 'deal_variable' },
    { path: 'due_date_variable_id', type: 'tenant_variable', nameField: 'due_date_variable' },
    { path: 'cpf_cnpj_variable_id', type: 'tenant_variable', nameField: 'cpf_cnpj_variable' },
  ],
};

/**
 * Interface para mapeamento de campos de trigger
 * 
 * RELEASE: 1.1.0 - 2026-02-03 - Expandido para suporte completo ao crm_filter
 */
export interface TriggerFieldMapping {
  /** Caminho do campo no trigger_config (ex: 'workspace_id') */
  path: string;
  /** Tipo de referência externa */
  type: ExternalReferenceType;
  /** Se o campo é um array de UUIDs */
  isArray: boolean;
  /** Campo correspondente com o nome amigável (ex: 'workspace_name') */
  nameField?: string;
}

/**
 * Campos de trigger_config que podem conter UUIDs
 * Mapeamento completo para exportação/importação de gatilhos
 * 
 * RELEASE: 1.0.0 - 2026-02-03 - Mapeamento inicial
 * RELEASE: 1.1.0 - 2026-02-03 - Expandido para suporte completo ao crm_filter
 */
export const TRIGGER_REFERENCE_FIELDS: TriggerFieldMapping[] = [
  // ========================================
  // GATILHOS GERAIS
  // ========================================
  { path: 'inbox_ids', type: 'inbox', isArray: true, nameField: 'inbox_names' },
  
  // ========================================
  // GATILHO CRM_FILTER - DEALS
  // ========================================
  { path: 'workspace_id', type: 'workspace', isArray: false, nameField: 'workspace_name' },
  { path: 'pipeline_id', type: 'pipeline', isArray: false, nameField: 'pipeline_name' },
  { path: 'phase_ids', type: 'phase', isArray: true, nameField: 'phase_names' },
  { path: 'seller_id', type: 'seller', isArray: false, nameField: 'seller_name' },
  { path: 'assigned_to', type: 'agent', isArray: false, nameField: 'assigned_to_name' },
  { path: 'deal_tag_ids', type: 'deal_tag', isArray: true, nameField: 'deal_tag_names' },
  
  // ========================================
  // GATILHO CRM_FILTER - CONVERSAS
  // ========================================
  { path: 'department_id', type: 'department', isArray: false, nameField: 'department_name' },
  { path: 'department_ids', type: 'department', isArray: true, nameField: 'department_names' },
  { path: 'kanban_category_id', type: 'kanban_category', isArray: false, nameField: 'kanban_category_name' },
  { path: 'kanban_phase_ids', type: 'kanban_phase', isArray: true, nameField: 'kanban_phase_names' },
  { path: 'contact_tag_ids', type: 'contact_tag', isArray: true, nameField: 'contact_tag_names' },
  { path: 'tag_ids', type: 'contact_tag', isArray: true, nameField: 'tag_names' },
];
